import { useState, useCallback, useMemo, forwardRef, useImperativeHandle, Fragment } from 'react';
import {
  IoSaveOutline, IoTrashOutline,
  IoCreateOutline, IoCloseOutline,
  IoChevronForward, IoChevronDown,
  IoArrowUp, IoArrowDown, IoSwapVertical,
} from 'react-icons/io5';
import { Scrollbars } from 'rc-scrollbars';
import { Modal as ConfirmModal } from '..';
import BestStartButton from '../button';

const RowEditorLayout = forwardRef(function RowEditorLayout({
  drafts,
  idField,
  allowEdit = true,
  canDelete = true,
  deleteDisabledTooltip,
  savingId,
  dirtyIndices,
  confirmDelete,
  setConfirmDelete,
  deleting,
  handleAdd,
  handleSave,
  handleSaveAll,
  handleCancel,
  handleRemove,
  emptyLabel,
  deleteTestId,
  deleteTitle,
  deleteText,
  icon: Icon,
  gridClassName,
  rowClassName,
  getRowClassName,
  renderFields,
  renderExtra,
  renderToolbar,
  displayOrder,
  tableColumns,
  rowClickExpand = false,
  sortKey,
  sortDir,
  onSort,
  renderActions,
  renderEditActions,
  onBeforeEditRow,
  singleEdit = false,
  scrollable = false,
}, ref) {
  const [editingKeys, setEditingKeys] = useState(() => new Set());
  const [expandedKeys, setExpandedKeys] = useState(() => new Set());
  const [confirmCancel, setConfirmCancel] = useState(null);

  const startEdit = useCallback(async (key, item, index) => {
    const allowed = await onBeforeEditRow?.(item, index, key);
    if (allowed === false) return;
    if (!singleEdit) {
      setEditingKeys((prev) => new Set(prev).add(key));
      return;
    }

    const openIndex = drafts.findIndex((draft, i) => editingKeys.has(draft?.[idField] ?? `new-${i}`));
    if (openIndex !== -1 && openIndex !== index) {
      const openKey = drafts[openIndex]?.[idField] ?? `new-${openIndex}`;
      if (dirtyIndices?.has(openIndex)) {
        setConfirmCancel({ index: openIndex, key: openKey, next: key });
        return;
      }
      handleCancel(openIndex);
    }
    setEditingKeys(new Set([key]));
  }, [onBeforeEditRow, singleEdit, drafts, editingKeys, idField, dirtyIndices, handleCancel]);

  const stopEdit = useCallback((key) => {
    setEditingKeys((prev) => {
      const next = new Set(prev);
      next.delete(key);
      return next;
    });
  }, []);

  const toggleExpand = useCallback((key) => {
    setExpandedKeys((prev) => (prev.has(key) ? new Set() : new Set([key])));
  }, []);

  const onSaveRow = useCallback(async (index, key) => {
    const ok = await handleSave(index);
    if (ok) stopEdit(key);
  }, [handleSave, stopEdit]);

  const onCancelRow = useCallback((index, key) => {
    handleCancel(index);
    stopEdit(key);
  }, [handleCancel, stopEdit]);

  const requestCancelRow = useCallback((index, key) => {
    if (dirtyIndices?.has(index)) {
      setConfirmCancel({ index, key });
    } else {
      onCancelRow(index, key);
    }
  }, [dirtyIndices, onCancelRow]);

  const onSaveAll = useCallback(async () => {
    await handleSaveAll();
    setEditingKeys(new Set());
  }, [handleSaveAll]);

  const confirmDiscard = useCallback(() => {
    onCancelRow(confirmCancel.index, confirmCancel.key);
    if (confirmCancel.next) setEditingKeys(new Set([confirmCancel.next]));
    setConfirmCancel(null);
  }, [confirmCancel, onCancelRow]);

  useImperativeHandle(ref, () => ({ add: handleAdd, saveAll: onSaveAll }), [handleAdd, onSaveAll]);

  const order = useMemo(() => {
    const base = displayOrder ?? drafts.map((_, i) => i);
    return [...base].sort((a, b) => {
      const aNew = drafts[a]?.[idField] == null;
      const bNew = drafts[b]?.[idField] == null;
      if (aNew !== bNew) return aNew ? -1 : 1;
      return base.indexOf(a) - base.indexOf(b);
    });
  }, [displayOrder, drafts, idField]);

  const renderReadOnlyActions = useCallback((item, index, key, canEditRow) => {
    if (renderActions) {
      return renderActions({
        item,
        index,
        key,
        canEditRow,
        startEdit,
        setConfirmDelete,
      });
    }

    return (
      <>
        {canEditRow && (
          <BestStartButton aria-label="Edit" tooltip="Edit" content={<IoCreateOutline size={16} />} color="blue" height={34} width={44} onClick={() => { startEdit(key, item, index); }} data-testid={`${deleteTestId}-edit`} />
        )}
        {(() => {
          const deletable = typeof canDelete === 'function' ? canDelete(item) : canDelete;
          const tooltip = (!deletable ? (typeof deleteDisabledTooltip === 'function' ? deleteDisabledTooltip(item) : deleteDisabledTooltip) : undefined) || 'Delete';
          return <BestStartButton aria-label="Delete" tooltip={tooltip} content={<IoTrashOutline size={16} />} color="red" height={34} width={44} onClick={() => setConfirmDelete({ [idField]: item[idField], index })} disabled={!deletable} data-testid={deleteTestId} />;
        })()}
      </>
    );
  }, [renderActions, startEdit, deleteTestId, idField, canDelete, deleteDisabledTooltip]);

  if (tableColumns) {
    const colSpan = tableColumns.length + 2;
    const tableEl = (
      <table className="row-table">
            <thead>
              <tr>
                <th className="row-table__expand-col" aria-label="Expand" />
                {tableColumns.map((col) => {
                  const sortable = col.sortable !== false && onSort;
                  const active = sortKey === col.key;
                  return (
                    <th
                      key={col.key}
                      className={`${col.className ?? ''}${sortable ? ' row-table__th--sortable' : ''}${active ? ' row-table__th--active' : ''}`.trim() || undefined}
                      onClick={sortable ? () => onSort(col.key) : undefined}
                      aria-sort={active ? (sortDir === 'asc' ? 'ascending' : 'descending') : undefined}
                    >
                      <span className="row-table__th-inner">
                        {col.label}
                        {sortable && (
                          <span className="row-table__sort-icon">
                            {active ? (sortDir === 'asc' ? <IoArrowUp size={13} /> : <IoArrowDown size={13} />) : <IoSwapVertical size={13} />}
                          </span>
                        )}
                      </span>
                    </th>
                  );
                })}
                <th className="row-table__actions-col" aria-label="Actions" />
              </tr>
            </thead>
            <tbody>
              {drafts.length === 0 && (
                <tr><td colSpan={colSpan} className="row-editor-empty">{emptyLabel}</td></tr>
              )}
              {drafts.length > 0 && order.length === 0 && (
                <tr><td colSpan={colSpan} className="row-editor-empty">{emptyLabel}</td></tr>
              )}
              {order.map((index) => {
                const item = drafts[index];
                const isNew = item[idField] == null;
                const key = item[idField] ?? `new-${index}`;
                const editing = isNew || editingKeys.has(key);
                const expanded = editing || expandedKeys.has(key);
                const canEditRow = typeof allowEdit === 'function' ? allowEdit(item) : allowEdit;
                const clickToExpand = rowClickExpand && !editing;
                const additionalRowClass = getRowClassName?.(item);
                return (
                  <Fragment key={key}>
                    <tr
                      className={`row-table__row${expanded ? ' row-table__row--expanded' : ''}${editing ? ' row-table__row--editing' : ''}${clickToExpand ? ' row-table__row--clickable' : ''}${additionalRowClass ? ` ${additionalRowClass}` : ''}`}
                      onClick={clickToExpand ? () => toggleExpand(key) : undefined}
                    >
                      <td className="row-table__expand-col">
                        <span
                          role="button"
                          tabIndex={editing ? -1 : 0}
                          className={`row-table__expand-btn${editing ? ' row-table__expand-btn--disabled' : ''}`}
                          onClick={(e) => { e.stopPropagation(); if (!editing) toggleExpand(key); }}
                          onKeyDown={(e) => { if (!editing && (e.key === 'Enter' || e.key === ' ')) { e.preventDefault(); e.stopPropagation(); toggleExpand(key); } }}
                          aria-expanded={expanded}
                          aria-disabled={editing}
                          aria-label={expanded ? 'Collapse row' : 'Expand row'}
                        >
                          {expanded ? <IoChevronDown size={16} /> : <IoChevronForward size={16} />}
                        </span>
                      </td>
                      {tableColumns.map((col) => (
                        <td key={col.key} className={col.className} data-label={col.label}>
                          {col.render ? col.render(item, index) : item[col.key]}
                        </td>
                      ))}
                      <td className="row-table__actions-col" onClick={(e) => e.stopPropagation()}>
                        <div className="document-edit-actions">
                          {editing ? (
                            <>
                              {renderEditActions?.({ item, index, key })}
                              <BestStartButton aria-label="Save" content={<IoSaveOutline size={16} />} color="green" height={34} width={44} onClick={() => onSaveRow(index, key)} disabled={savingId !== null} data-testid={`${deleteTestId}-save`} />
                              <BestStartButton aria-label="Cancel" content={<IoCloseOutline size={16} />} color="red" height={34} width={44} onClick={() => requestCancelRow(index, key)} disabled={savingId !== null} data-testid={`${deleteTestId}-cancel`} />
                            </>
                          ) : (
                            renderReadOnlyActions(item, index, key, canEditRow)
                          )}
                        </div>
                      </td>
                    </tr>
                    {expanded && (
                      <tr className="row-table__detail-row">
                        <td colSpan={colSpan}>
                          <div className={`document-edit-grid${gridClassName ? ` ${gridClassName}` : ''}`}>
                            {renderFields(item, index, editing)}
                          </div>
                          {renderExtra?.(item, index, editing)}
                        </td>
                      </tr>
                    )}
                  </Fragment>
                );
              })}
            </tbody>
          </table>
    );
    return (
      <div className="row-editor">
        {renderToolbar?.()}
        <div className="row-table-wrap">
          {scrollable ? (
            <Scrollbars
              autoHide
              style={{ height: '100%' }}
              renderThumbVertical={(props) => <div {...props} className="thumb-vertical" />}
            >
              {tableEl}
            </Scrollbars>
          ) : (
            tableEl
          )}
        </div>
        <ConfirmModal title={deleteTitle} text={deleteText} confirmButtonText="Delete" cancelButtonText="Cancel" confirmColor="red" cancelColor="blue" onConfirm={handleRemove} onClose={() => setConfirmDelete(null)} open={Boolean(confirmDelete)} loading={deleting} />
        <ConfirmModal title="Discard unsaved changes?" text="This row has unsaved changes. Are you sure you want to discard them?" confirmButtonText="Discard" cancelButtonText="Keep Editing" confirmColor="red" cancelColor="blue" onConfirm={confirmDiscard} onClose={() => setConfirmCancel(null)} open={Boolean(confirmCancel)} />
      </div>
    );
  }

  return (
    <div className="row-editor">
      {renderToolbar?.()}
      <ul className="document-edit-list">
        {drafts.length === 0 && <li className="row-editor-empty">{emptyLabel}</li>}
        {drafts.length > 0 && order.length === 0 && <li className="row-editor-empty">No matching results</li>}
        {order.map((index) => {
          const item = drafts[index];
          const isNew = item[idField] == null;
          const key = item[idField] ?? `new-${index}`;
          const editing = isNew || editingKeys.has(key);
          const canEditRow = typeof allowEdit === 'function' ? allowEdit(item) : allowEdit;
          return (
            <li key={key} className={`document-edit-row${editing ? ' document-edit-row--editing' : ''}${rowClassName ? ` ${rowClassName}` : ''}`}>
              <span className="document-icon"><Icon /></span>
              <div className={`document-edit-grid${gridClassName ? ` ${gridClassName}` : ''}`}>
                {renderFields(item, index, editing)}
              </div>
              <div className="document-edit-actions">
                {editing ? (
                  <>
                    {renderEditActions?.({ item, index, key })}
                    <BestStartButton aria-label="Save" content={<IoSaveOutline size={16} />} color="green" height={34} width={44} onClick={() => onSaveRow(index, key)} disabled={savingId !== null} data-testid={`${deleteTestId}-save`} />
                    <BestStartButton aria-label="Cancel" content={<IoCloseOutline size={16} />} color="red" height={34} width={44} onClick={() => requestCancelRow(index, key)} disabled={savingId !== null} data-testid={`${deleteTestId}-cancel`} />
                  </>
                ) : (
                  renderReadOnlyActions(item, index, key, canEditRow)
                )}
              </div>
              {renderExtra?.(item, index, editing)}
            </li>
          );
        })}
      </ul>
      <ConfirmModal title={deleteTitle} text={deleteText} confirmButtonText="Delete" cancelButtonText="Cancel" confirmColor="red" cancelColor="blue" onConfirm={handleRemove} onClose={() => setConfirmDelete(null)} open={Boolean(confirmDelete)} loading={deleting} />
      <ConfirmModal title="Discard unsaved changes?" text="This row has unsaved changes. Are you sure you want to discard them?" confirmButtonText="Discard" cancelButtonText="Keep Editing" confirmColor="red" cancelColor="blue" onConfirm={confirmDiscard} onClose={() => setConfirmCancel(null)} open={Boolean(confirmCancel)} />
    </div>
  );
});

export default RowEditorLayout;
